import "./AccountInformationComponent.scss";
import template from "./AccountInformationComponent.html";
import {IComponentOptions} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {ShareAccount} from "../../types/AccountManagementRecords";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {EquityAccountRecord} from "../../equity/types/EquityAccountRecord";
import {AccountManagementService} from "../../services/AccountManagmentService";
import {
    ClientNotesViewModel,
    ClientNotesViewModels
} from "../../equity/components/client-notes/models/ClientNotesViewModel";
import {ClientNotesViewStore} from "../../equity/components/client-notes/store/ClientNotesViewStore";
import {ClientNotesService} from "../../equity/components/client-notes/service/ClientNotesService";

export const AccountInformation = "tfAccountInformation";

export function accountInformation(): IComponentOptions {
    return {
        template,
        bindings: {
            account: "<"
        },
        controllerAs: "ctrl",
        controller: AccountInformationComponentController
    };
}

export class AccountInformationComponentController {

    private readonly tradingAccountDetails$: Observable<ShareAccount>;
    private readonly accountType$: Observable<string | null>;
    private readonly clientType$: Observable<string | null>;
    private readonly exemptionType$: Observable<string | null>;
    private readonly exemptionCertificateExpiry$: Observable<string | null>;
    private readonly portfolioFrequency$: Observable<string | null>;

    /** @input */
    public set account(account: EquityAccountRecord | null) {
        if (account != null) {
            this.account$.next(account);
        }
    }

    private readonly account$ = new ReplaySubject<EquityAccountRecord>(1);
    public readonly models$: Observable<ClientNotesViewModels>;
    private readonly store: ClientNotesViewStore;

    /** @ngInject */
    public constructor(private readonly accountManagementService: AccountManagementService,
                       clientNotesService: ClientNotesService) {
        this.store = new ClientNotesViewStore(clientNotesService);
        this.models$ = this.store
                           .notes
                           .map(
                               (records) => records
                                   .filter(r => r.noteTag === "Portfolio Review")
                                   .filter((r, i) => i === 0)
                                   .map((r) => ClientNotesViewModel.of(r))
                           );

        this.tradingAccountDetails$ = this.account$
                                          .map((account) => {
                                              this.store.updateAccountId(account.id);
                                              return account.accountNumber;
                                          })
                                          .switchMap((account) => {
                                              return this.retrieveShareAccountDetails(account);
                                          })
                                          .map((details) => details)
                                          .filter(isNotNull)
                                          .publishReplay(1)
                                          .refCount();
        // Add logging inside the subscription
        this.tradingAccountDetails$.subscribe({
                                                  next: (details) => {
                                                      console.log("Trading account details received:", details);
                                                  },
                                                  error: (err) => {
                                                      console.error("Error fetching trading account details:", err);
                                                  },
                                                  complete: () => {
                                                      console.log("Trading account details subscription completed");
                                                  }
                                              });

        this.accountType$ = this.tradingAccountDetails$.map(e => e.accountType);
        this.clientType$ = this.tradingAccountDetails$.map(e => e.clientType);
        this.exemptionType$ = this.tradingAccountDetails$.map(e => e.exemptionType);
        this.exemptionCertificateExpiry$ = this.tradingAccountDetails$.map(e => e.exemptionCertificateExpiry);
        this.portfolioFrequency$ = this.tradingAccountDetails$.map(e => e.portfolioFrequency);
    }

    private retrieveShareAccountDetails(accountId: string): Observable<ShareAccount> {
        return Observable.of(accountId)
                         .flatMap((i) => this.accountManagementService.detail(accountId))
                         .catch((err) => Observable.empty());
    }
}
